import { getWindow } from '@databases/connect';
import { logError } from '@databases/logs';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { isEmbed } from '@databases/utils/utils';

// This will be re-enabled with @databases/redux introduction
//import { DefaultRootState } from '@databases/redux';
import { AnalyticsClient } from './AnalyticsClient';
import {
  DatabaseContainerType,
  SendDatabaseDeleteEntryEventSources,
  SendDatabaseSortEventSources,
} from './types';

type DefaultRootState = any;

export const sendSortEvent = async (
  client: AnalyticsClient,
  sort: DatabaseViewSettings['sort'],
  source: SendDatabaseSortEventSources,
  type?: DatabaseFieldType,
  contentId?: string
): Promise<void> => {
  // if sort is reset, it will be undefined. we still want to record this event.
  if (sort === undefined) {
    await client.sendDatabaseSortedEvent({
      contentId: contentId,
      source: source,
      databaseContainer: getDatabaseContainerType(),
      fieldType: type,
    });
  } else {
    // if sort is defined, we want to make sure the following values are defined.
    // if they are not defined, it is an error.
    if (sort.fieldId === undefined) {
      logError(
        new Error('sendDatabaseSortedEvent was not sent because sortId was not defined')
      );
      return;
    }
    if (sort.ascending === undefined) {
      logError(
        new Error(
          'sendDatabaseSortedEvent was not sent because isAscending was not defined'
        )
      );
      return;
    }

    await client.sendDatabaseSortedEvent({
      contentId: contentId,
      fieldId: sort.fieldId,
      isAscending: sort.ascending,
      source: source,
      databaseContainer: getDatabaseContainerType(),
      fieldType: type,
    });
  }
};

export const sendDeleteEntryEvent = async (
  client: AnalyticsClient,
  source: SendDatabaseDeleteEntryEventSources,
  state: DefaultRootState,
  contentId: string
): Promise<void> => {
  await client.sendDeleteEntryEvent({
    contentId,
    source: source,
    databaseContainer: getDatabaseContainerType(),
  });
};

const isEntry = (win?: Window): boolean => {
  const targetWindow = win || window;
  const url = new URLSearchParams(targetWindow.location.search);
  return !!(url.get('entryId') && !url.get('fieldId'));
};

const isValue = (): boolean => {
  const url = new URLSearchParams(window.location.search);
  return !!(url.get('entryId') && url.get('fieldId'));
};

export const getDatabaseContainerType = (): DatabaseContainerType => {
  if (isEmbed()) {
    if (isEntry()) {
      return DatabaseContainerType.EMBED_ENTRY;
    } else if (isValue()) {
      return DatabaseContainerType.EMBED_VALUE;
    } else {
      return DatabaseContainerType.EMBED_DATABASE;
    }
  } else {
    // for standalone, we want to get the parent window because the iframe won't
    // have the query params.
    // see: https://bitbucket.org/atlassian/canvas/pull-requests/12217#comment-492835327
    if (isEntry(getWindow())) {
      return DatabaseContainerType.STANDALONE_ENTRY;
    } else {
      return DatabaseContainerType.STANDALONE_DATABASE;
    }
  }
};
